import React, { useEffect, useState, useMemo } from "react";
import axios from 'axios';
import Navbar from '../elements/Navbar';
import StagedProductCard from '../components/StagedProductCard';
import Pagination from '../components/Pagination';
import SearchBar from '../components/SearchBar';
import DialogBox from '../components/DialogBox';

const StagedProductReview = () => {
  const [vendorData, setVendorData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogBoxTitle, setDialogBoxTitle] = useState('');
  const [dialogBoxMessage, setDialogBoxMessage] = useState('');
  const API_BASE = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_API_URL_BASE_DEV : process.env.REACT_APP_API_URL_BASE_PROD;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/vendors/attribute_bundle`);
        setVendorData(response.data)
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    }

    setCurrentPage(1);
    fetchProducts();
  }, [API_BASE]);

  const handleReEnrich = async (vendorId) => {
    try {
      // Step 1: Trigger enrichment
      // await axios.post(`${API_BASE}/api/staged_product_attributes/re_enrich`, {
      //   shopify_product_id: shopifyProductId,
      // });
      console.log('Re-enriching vendor with ID: ', vendorId);

      await axios.post(`${API_BASE}/api/vendors/${vendorId}/re_enrich`);
  
      // Step 2: Fetch updated vendor data
      const response = await axios.get(`${API_BASE}/api/vendors/${vendorId}/attribute_bundle`);
      const updatedVendor = response.data;
  
      // Step 3: Update state
      setVendorData(prev => {
        return prev.map(v =>
          v.vendor === updatedVendor.vendor ? updatedVendor : v
        );
      });
    } catch (error) {
      console.error('Failed to re-enrich product:', error);
    }
  };

  const handleTriggerVendorImport = async () => {
    const response = await axios.post(`${API_BASE}/api/vendors/import_vendors`);

    if (response.status === 200) {
      setDialogBoxTitle('Vendor Import Triggered');
      setDialogBoxMessage('The vendor import has been successfully triggered. Please check back later for updates.');
      setIsDialogOpen(true);
    }
    else {
      console.error('Failed to trigger vendor import:', response.statusText);
    }
  };

  const filteredVendorData = useMemo(() => {
    if (!searchQuery) return vendorData;
  
    const query = searchQuery.toLowerCase();
    return vendorData.filter(vendor =>
      vendor.vendor.toLowerCase().includes(query) ||
      vendor.products.some(product =>
        product.title.toLowerCase().includes(query)
      )
    );
  }, [vendorData, searchQuery]);

  const paginatedVendors = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredVendorData.slice(startIndex, startIndex + pageSize);
  }, [filteredVendorData, currentPage, pageSize]);

  const renderStagedProductCards = () => {
    if (vendorData.length === 0) {
      return <p>No vendors found.</p>;
    }
    
    return (
        <div className="p-4 space-y-6">
          {paginatedVendors.map((vendor, id) => (
              <StagedProductCard 
                key={id} 
                vendor={vendor}
                productType={vendor.product_type} 
                sharedAttributes={vendor.shared_attributes}
                products={vendor.products}
                onReEnrich={() => handleReEnrich(vendor.id)}
              />
            )
          )}
        </div>
      )
  }

  return (
    <div>
      <Navbar />
      <div className="container mx-auto mb-32">
        <div className="updaterWrapper">
          <div className="space-y-12 border-b border-gray-900/10 pb-12">
            <h1 className="text-2xl font-semibold leading-7 text-gray-900">AI Products Review</h1>
            <p className="leading-6" style={{marginTop: "1rem"}}>
              The <strong>Staged Product Review</strong> feature is a workflow for reviewing and approving AI-enriched product attributes before they are published to Shopify. When a new product is created in Shopify, a webhook triggers our enrichment pipeline, which uses a series of <strong>AI services</strong> to populate product and variant attributes such as brand, device name, battery capacity, and more. These attributes are stored in a local database in a “staged” state and surfaced in the admin UI for manual review. 
            </p>
            <p className="leading-6" style={{marginTop: "1rem"}}>
              Team members can edit individual fields, mark them as approved, and save changes without reloading the full product. Once saved, only the approved attributes are batched and synced to Shopify using the GraphQL metafieldsSet API. This system ensures that product data is complete, standardized, and validated before being exposed to customers.
            </p>
            <p className="leading-6" style={{marginTop: "1rem"}}>
              The Staged Product Review feature is in active production and will continue to evolve as we refine our product data model and enrichment logic. At this time, only <strong>product-level metafields</strong> are updated via the sync process—<strong>product variant metafields are not yet handled</strong>, but support for this is planned. Expect frequent improvements as we integrate new AI extractors, expand support for variant data, and enhance the UI for faster approvals.
            </p>
          </div>
        </div>
        <DialogBox
          isOpen={isDialogOpen}
          setIsOpen={() => setIsDialogOpen(false)}
          title={dialogBoxTitle}
          message={dialogBoxMessage}
        />
        <div className="productSearchWrapper mt-11 w-1/2 mx-auto">
          <SearchBar
            value={searchQuery}
            onChange={(val) => {
              setSearchQuery(val);
              setCurrentPage(1);
            }}
            placeholder="Search by vendor or title"
          />
        </div>
        <div className="paginationWrapper flex justify-between items-center mt-6 border-b border-gray-200 bg-white px-4 pt-1 pb-2 sm:px-6">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredVendorData.length}
            pageSize={pageSize}
            onPageChange={(page) => setCurrentPage(page)}
          />
          <button
            onClick={handleTriggerVendorImport}
            className="my-4 mx-6 bg-indigo-600 text-white px-4 py-2 rounded-md"
          >
            Import Vendors
          </button>
        </div>
        <div className="productSearchWrapper mt-11 mx-auto">
          {renderStagedProductCards()}
        </div>
        <div className="paginationWrapper flex justify-between items-center mt-6 border-b border-gray-200 bg-white px-4 pt-1 pb-2 sm:px-6">
          <Pagination
            currentPage={currentPage}
            totalItems={filteredVendorData.length}
            pageSize={pageSize}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    </div>
  );
};

export default StagedProductReview;
